import { Block } from '@/declare/interface/geometry/block';
import './index.less';
import { ContextMenu, MenuItem, MenuList, SideHeader, TnModal } from 'bimui';
import { ReactComponent as CustomizeSvg } from 'bimui/dist/c1/icons/customize.svg';
import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { deleteBlockMulti, findBlocksInProject } from '@/declare/api/geometry/block';
import { message } from 'antd';
import classNames from 'classnames';
import { emitter } from '@/utils/mitt';
import { Emit_Event_Common_Tool } from '@/declare/enums/emit-event';
import { BaseUtils } from 'tnbase';
import { EngineContext, RegisterContext } from '@/App';
import { DataChangeStruct, PythaEventBlock, PythaEventName } from 'pytha2';
import { SIDEBAR_TYPE } from '@/declare/enums/menu-type';

export default function BlockPanel() {
    const { projectUuid } = useParams();
    const engineContext = useContext(EngineContext);
    const location = useLocation();
    const navigate = useNavigate();
    const registerContext = useContext(RegisterContext);

    const [blocks, setBlocks] = useState<Block[]>([]);
    const [selectedUuids, setSelectedUuids] = useState<string[]>([]);   // 转中的uuid
    const lastClickIdxRef = useRef<number>(null);
    const [deleteEventUuid, setDeleteEventUuid] = useState<string>(null);
    // 右键
    const [cmOpen, setCmOpen] = useState<boolean>(false);
    const [cmTop, setCmTop] = useState<number>();
    const [cmLeft, setCmLeft] = useState<number>();

    const [loading, setLoading] = useState<boolean>(false);
    const projectUuidRef = useRef<string>(projectUuid);
    const isDbClickRef = useRef<boolean>(false);

    useEffect(() => {
        emitter.on(Emit_Event_Common_Tool.DELETE, onListenDelete);

        return () => {
            emitter.off(Emit_Event_Common_Tool.DELETE, onListenDelete);
        }
    }, [])

    useEffect(() => {
        if (!engineContext.engine) return;
        const unsubscribe = engineContext.engine.eventMediator.noticeHandler?.onNotice?.subscribe((sender, event) => {
            if (event.cmd === PythaEventName.DATA_CHANGE) {
                const path = location.pathname;
                if (path.includes("axis-net")) return; // 阻止来自编辑轴网页签的事件
                if (path.includes("section-detail")) return; // 阻止来自编辑截面页签的事件

                let data = event?.data as DataChangeStruct;
                if (!data) return;
                if (data.add_block_template != null || data.delete_block_template != null) {
                    setTimeout(() => {
                        generateData(projectUuidRef.current);
                    }, 100)
                }
            }
        });

        return () => {
            unsubscribe?.();
        }
    }, [engineContext.engine])

    useEffect(() => {
        if (projectUuid == null) return;
        projectUuidRef.current = projectUuid;
        generateData(projectUuid);
    }, [projectUuid])

    useEffect(() => {
        if (deleteEventUuid == null) return;
        onClickDelete();
    }, [deleteEventUuid])

    // 监听删除事件
    const onListenDelete = () => {
        setDeleteEventUuid(BaseUtils.genUUID());
    }

    const generateData = (projectUuid) => {
        findBlocksInProject(projectUuid).then(res => {
            setBlocks(res.data || []);
        }).catch(err => {
            message.error("获取块列表失败")
        })
    }


    // 删除
    const onClickDelete = () => {
        if (selectedUuids.length == 0 || !blocks || blocks.length == 0) return;
        setCmOpen(false);
        if (loading) return;
        TnModal().deleteModal({
            onOk: () => {
                setLoading(true);
                deleteBlockMulti(selectedUuids).then(res => {
                    setSelectedUuids([]);
                    setBlocks(blocks.filter(b => !selectedUuids.includes(b.uuid)));
                }).catch(err => {
                    message.error("删除失败")
                }).finally(() => {
                    TnModal().destoryAll();
                    setLoading(false);
                })
            }
        })
    }

    // 点击数据行
    const onClickRow = (e, uuid, index) => {
        isDbClickRef.current = false;
        setTimeout(() => {
            if (isDbClickRef.current) return;
            let uuids = [...selectedUuids];
            if (e.ctrlKey) {
                if (uuids.includes(uuid)) {
                    uuids = uuids.filter(u => u !== uuid);
                } else {
                    uuids.push(uuid);
                }
            } else if (e.shiftKey && lastClickIdxRef.current != null) {
                let start = Math.max(0, Math.min(index, lastClickIdxRef.current));
                let end = Math.min(blocks.length - 1, Math.max(index, lastClickIdxRef.current));
                for (let i = start; i <= end; i++) {
                    if (!uuids.includes(blocks[i].uuid)) {
                        uuids.push(blocks[i].uuid);
                    }
                }
            } else {
                if (uuids.includes(uuid)) {
                    uuids = [];
                } else {
                    uuids = [uuid];
                }
            }
            lastClickIdxRef.current = index;
            setSelectedUuids(uuids);
        }, 100)
    }

    // 右键
    const onContextMenu = (e, uuid) => {
        e.preventDefault();
        e.stopPropagation();
        if (!selectedUuids.includes(uuid)) {
            setSelectedUuids([...selectedUuids, uuid]);
        }
        setCmOpen(true);
        setCmLeft(e.clientX);
        setCmTop(e.clientY);
    }

    // 双击
    const onDbClick = (e, uuid) => {
        e.preventDefault();
        e.stopPropagation();
        isDbClickRef.current = true;
        registerContext.activeTool(null);
        registerContext.activeSidebar(SIDEBAR_TYPE.BLOCK);
        navigate(`/projects/${projectUuid}/blocks/${uuid}`);
    }

    // 点击布置
    const onClickArrange = () => {
        if (selectedUuids.length !== 1) return;
        const block = blocks.find(b => selectedUuids.includes(b.uuid));
        if (!block) return;
        if (!engineContext.engine) return;
        engineContext?.emitCommand("ARRANGE_BLOCK");
        let event = new PythaEventBlock();
        event.data = { "blockTemplateUuid": block.uuid };
        engineContext?.engine?.eventMediator?.emitEvent(event);
    }

    return <div className="block-panel">
        <SideHeader>
            <div className='header'>
                <span>块</span>
                {selectedUuids.length === 1 && <CustomizeSvg className='add-icon' onClick={onClickArrange} />}
            </div>
        </SideHeader>
        <div className='content'>
            {blocks.map((block, index) => {
                return <div key={block?.uuid} className={classNames({
                    'block-row ellipsis': true,
                    'active': selectedUuids.includes(block.uuid)
                })} onClick={(e) => onClickRow(e, block.uuid, index)}
                    onDoubleClick={(e) => onDbClick(e, block?.uuid)}
                    onContextMenu={(e) => onContextMenu(e, block?.uuid)}>{block?.properties?.name}</div>
            })}
        </div>
        <ContextMenu
            visible={cmOpen} // 是否显示
            top={cmTop} // 菜单纵坐标
            left={cmLeft} // 菜单横坐标
            wrapperClassname='section-list-right-menu' // 自定义右键菜单样式
            onCancel={() => {
                setCmOpen(false); // 关闭右键菜单
            }}
        >
            <MenuList>
                <MenuItem onClick={onClickDelete} special>
                    删除
                </MenuItem>
            </MenuList>
        </ContextMenu>
    </div>
}